import { useFormContext, useWatch } from 'react-hook-form';
import { MenuItem, Stack, Typography } from '@mui/material';
import {
  packageTypes,
  terminationTypeOptionsExpanded,
  TerminationTypeValues,
} from 'types/part';
import {
  HookFormSelect,
  HookFormToggle,
  HookQuerySingleFieldSearchSelect,
} from 'ui-component/HookFormComponents';
import { OrgPartFormConstants } from 'ui-component/CreateNewOrgPartDialog/constants';
import PackageAutocomplete from 'ui-component/PackageAutocomplete';
import { MslType, PublicClassificationSchema } from 'types/clientV2/parts';
import { useEffect, useMemo, useState } from 'react';
import { PaddedDivider } from 'ui-component/CreateNewOrgPartDialog/components/CreatePartForm';
import { useGetPublicClassificationsQuery } from 'store/slices/clientV2/parts';
import { ALL, QueryFilterOperators } from 'types/api';
import _ from 'lodash';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';
import {
  DimensionClassOptionLabels,
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import { HookFormUnitOfMeasureAutocomplete } from 'ui-component/HookFormComponents/HookFormUnitOfMeasureAutocomplete';

type ManualPartDetailsProps = {
  onlyRenderClassification?: boolean;
};

const ManualPartDetails = ({
  onlyRenderClassification = false,
}: ManualPartDetailsProps) => {
  const [selectedClassification, setSelectedClassification] =
    useState<PublicClassificationSchema | null>(null);
  const tempUoM = useFeature(FeatureFlags.tempUoM).on;

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const watchMountType = useWatch({
    control,
    name: OrgPartFormConstants.mountType.id,
  });

  const watchIsElectronicComponent = useWatch({
    control,
    name: OrgPartFormConstants.isElectronicComponent.id,
  });

  const watchPackageType = useWatch({
    control,
    name: OrgPartFormConstants.packageType.id,
  });

  const watchScrapedFields = useWatch({
    control,
    name: OrgPartFormConstants.scrapedFields.id,
  });

  const watchDimensionClass = useWatch({
    control,
    name: OrgPartFormConstants.dimensionClass.id,
  });

  const watchPreferredUnit = useWatch({
    control,
    name: OrgPartFormConstants.preferredUnit.id,
  });

  useEffect(() => {
    if (
      !_.includes(
        watchScrapedFields,
        OrgPartFormConstants.isElectronicComponent.id
      ) &&
      (watchDimensionClass !== DimensionClassOptions.QUANTITY ||
        watchPreferredUnit !== UnitOfMeasureOptions.PIECE)
    ) {
      setValue(OrgPartFormConstants.isElectronicComponent.id, false);
    }
  }, [watchDimensionClass, watchPreferredUnit, setValue, watchScrapedFields]);

  useEffect(() => {
    if (
      !_.includes(
        watchScrapedFields,
        OrgPartFormConstants.isElectronicComponent.id
      ) &&
      selectedClassification &&
      !selectedClassification.id?.startsWith('FREE_SOLO_OPTION') &&
      selectedClassification.isElectronicComponent != null
    ) {
      setValue(
        OrgPartFormConstants.isElectronicComponent.id,
        selectedClassification.isElectronicComponent
      );
    }
  }, [selectedClassification, setValue, watchScrapedFields]);

  const showSMTFields = useMemo(
    () =>
      [
        TerminationTypeValues.SMT,
        TerminationTypeValues.HYBRID_OF_PRESSED_FIT_AND_SMT,
        TerminationTypeValues.HYBRID_OF_SMT_AND_THT,
      ].includes(watchMountType) && watchIsElectronicComponent,
    [watchMountType, watchIsElectronicComponent]
  );

  return (
    <>
      <PaddedDivider />
      <HookQuerySingleFieldSearchSelect<PublicClassificationSchema>
        name={OrgPartFormConstants.classification.id}
        label={OrgPartFormConstants.classification.label}
        control={control}
        errors={errors}
        freeSolo
        mb={0}
        useGetQuery={useGetPublicClassificationsQuery}
        schema={[ALL]}
        searchSchema={['name']}
        fieldName="name"
        getOptionLabel={(option) => _.startCase(option)}
        filters={[
          {
            field: 'isPartClassification',
            operator: QueryFilterOperators['='],
            value: 'true',
          },
        ]}
        onSelectionChange={(selectedObject) => {
          setSelectedClassification(selectedObject);
        }}
        showAiIcon={_.includes(
          watchScrapedFields,
          OrgPartFormConstants.classification.id
        )}
      />
      {tempUoM && (
        <Stack direction="row" spacing={2}>
          <HookFormSelect
            control={control}
            name={OrgPartFormConstants.dimensionClass.id}
            label={OrgPartFormConstants.dimensionClass.label}
            errors={errors}
            options={Object.values(DimensionClassOptions).map((option) => ({
              value: option,
              label: DimensionClassOptionLabels[option],
            }))}
          />
          <HookFormUnitOfMeasureAutocomplete
            control={control}
            name={OrgPartFormConstants.preferredUnit.id}
            label={OrgPartFormConstants.preferredUnit.label}
            errors={errors}
            limitToDimensionClass={watchDimensionClass}
          />
        </Stack>
      )}
      {!onlyRenderClassification && (
        <>
          <Stack direction="row" alignItems="center" spacing={2}>
            <HookFormToggle
              name={OrgPartFormConstants.isElectronicComponent.id}
              showAiIcon={_.includes(
                watchScrapedFields,
                OrgPartFormConstants.isElectronicComponent.id
              )}
              label={
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {OrgPartFormConstants.isElectronicComponent.label}
                  </Typography>
                  <Typography>
                    Enable to show additional properties that are required for
                    parts used in PCBA or Wire Harnesses
                  </Typography>
                </Stack>
              }
              control={control}
              errors={errors}
              sx={{ ml: 1 }}
            />
          </Stack>
          {watchIsElectronicComponent && (
            <HookFormSelect
              control={control}
              name={OrgPartFormConstants.mountType.id}
              label={OrgPartFormConstants.mountType.label}
              errors={errors}
              sx={{ mb: 2, mt: 0 }}
              showAiIcon={_.includes(
                watchScrapedFields,
                OrgPartFormConstants.mountType.id
              )}
            >
              {terminationTypeOptionsExpanded.map((packageType) => (
                <MenuItem value={packageType.value} key={packageType.value}>
                  {packageType.label}
                </MenuItem>
              ))}
            </HookFormSelect>
          )}
          {showSMTFields && (
            <PackageAutocomplete
              name={OrgPartFormConstants.packageType.id}
              label={OrgPartFormConstants.packageType.label}
              errors={errors}
              handleSetValue={(option: string | null) => {
                setValue(OrgPartFormConstants.packageType.id, option);
              }}
              options={_.uniq([...packageTypes, watchPackageType])}
              initialValue={watchPackageType}
              mb={0}
              showAiIcon={_.includes(
                watchScrapedFields,
                OrgPartFormConstants.packageType.id
              )}
            />
          )}
          {showSMTFields && (
            <HookFormSelect
              control={control}
              name={OrgPartFormConstants.msl.id}
              label={OrgPartFormConstants.msl.label}
              errors={errors}
              sx={{ mb: 2, mt: 0 }}
              showAiIcon={_.includes(
                watchScrapedFields,
                OrgPartFormConstants.msl.id
              )}
            >
              {Object.values(MslType).map((mslValue) => (
                <MenuItem value={mslValue} key={mslValue}>
                  {mslValue}
                </MenuItem>
              ))}
            </HookFormSelect>
          )}
        </>
      )}
    </>
  );
};

export default ManualPartDetails;
